import commonEn from '@/pages/dashboard/_locales/enCN/common';
import commonZh from '@/pages/dashboard/_locales/zhCN/common';

/** metricsMap 中的维度/指标条目（与下载接口约定一致） */
export interface IMetricsMapItem {
  en: string;
  zh: string;
  type: string;
  downLoadFormatRule?: string | number;
}

/** 表格列（仅本工具关心的字段，调用方实际类型可宽于此） */
export interface IDimensionColumnLike {
  key: string;
  isDimColumn?: boolean;
  [k: string]: any;
}

/** OverallTableCo 中的 selectorDimension 描述（用于补充中英文） */
export interface ISelectorDimensionLike {
  key: string;
  labelEn?: string;
  labelCn?: string;
  defaultValue?: any;
  [k: string]: any;
}

/**
 * 根据维度 key 从国际化 locale 文件读取中英文名称
 * 优先取 `common_{key}`，回退到 key 本身
 */
export const getDimensionLabel = (dimKey: string): { en: string; zh: string; } => ({
  en: (commonEn as Record<string, string>)[`common_${dimKey}`] || dimKey,
  zh: (commonZh as Record<string, string>)[`common_${dimKey}`] || dimKey,
});

/**
 * 按页面 columns 的实际顺序提取 dimension_metrics
 * 规则：`isDimColumn === true` 且 `key !== 'date'`
 */
export const extractDimensionMetricsFromColumns = (
  columns: IDimensionColumnLike[] | undefined | null,
): string[] => {
  const result: string[] = [];
  columns?.forEach((col) => {
    if (!col?.isDimColumn || col?.key === 'date') return;
    result.push(col.key);
  });
  return result;
};

/**
 * 把 selectorDimensions 中"已选中"的维度补到 metricsMap（仅在不存在时补）
 * 用于 OverallTableCo 等支持自定义维度选择器的场景
 */
export const appendSelectorDimensionsToMetricsMap = (
  metricsMap: Record<string, IMetricsMapItem>,
  selectorDimensions: ISelectorDimensionLike[] | undefined | null,
  finalSelectorValues: Record<string, any> | undefined | null,
): void => {
  selectorDimensions?.forEach((d) => {
    const value = finalSelectorValues?.[d.key] ?? d.defaultValue;
    if (value && !metricsMap[d.key]) {
      metricsMap[d.key] = {
        en: d.labelEn || '',
        zh: d.labelCn || '',
        type: 'dimension',
      };
    }
  });
};

/**
 * 把 dimension_metrics 中的维度 key 补到 metricsMap（仅在不存在时补）
 * 中英文从 i18n locale 文件读取（`common_{key}`）
 */
export const appendDimensionMetricsToMetricsMap = (
  metricsMap: Record<string, IMetricsMapItem>,
  dimensionMetrics: string[] | undefined | null,
): void => {
  dimensionMetrics?.forEach((dimKey) => {
    if (!metricsMap[dimKey]) {
      const label = getDimensionLabel(dimKey);
      metricsMap[dimKey] = {
        en: label.en,
        zh: label.zh,
        type: 'dimension',
      };
    }
  });
};

/**
 * 一站式：从 columns 提取 dimensionMetrics 并把中英文补充到 metricsMap
 * 返回提取出的 dimensionMetrics 数组（用于拼装下载请求参数）
 *
 * @param columns 当前表格列（用于提取维度顺序）
 * @param metricsMap 指标 map（会被原地修改：补充缺失的维度中英文）
 * @param selectorDimensions 可选：自定义维度选择器配置
 * @param finalSelectorValues 可选：自定义维度选择器当前选中值
 */
export const buildDimensionMetricsAndAppendToMap = (
  columns: IDimensionColumnLike[] | undefined | null,
  metricsMap: Record<string, IMetricsMapItem>,
  selectorDimensions?: ISelectorDimensionLike[] | null,
  finalSelectorValues?: Record<string, any> | null,
): string[] => {
  const dimensionMetrics = extractDimensionMetricsFromColumns(columns);
  appendSelectorDimensionsToMetricsMap(metricsMap, selectorDimensions, finalSelectorValues);
  appendDimensionMetricsToMetricsMap(metricsMap, dimensionMetrics);
  return dimensionMetrics;
};
